import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCampaign, useUpdateCampaign } from "../../hooks/useCampaigns";
import { CampaignUpdate } from "../../types/campaign";
import AuditInfo from "../../components/ui/AuditInfo";
import { Input, Textarea } from "../../components/ui/Input";
import { auditApi } from "../../services/api";

export default function EditCampaignPage() {
  const { id } = useParams<{ id: string }>();
  const campaignId = Number(id);
  const navigate = useNavigate();
  const { data: c, isLoading } = useCampaign(campaignId);
  const updateMutation = useUpdateCampaign();
  const [form, setForm] = useState<CampaignUpdate>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (c) {
      setForm({
        client_id: c.client_id,
        name: c.name,
        description: c.description || "",
        program_id: c.program_id ?? undefined,
        bot_name: c.bot_name || "",
        color_palette: c.color_palette ?? undefined,
        avatar_url: c.avatar_url || "",
        max_auth_attempts: c.max_auth_attempts ?? undefined,
        session_timeout_seconds: c.session_timeout_seconds ?? undefined,
      });
    }
  }, [c]);

  const handleChange = (field: keyof CampaignUpdate, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        id: campaignId,
        data: {
          client_id: form.client_id,
          name: form.name || undefined,
          description: form.description || undefined,
          program_id: form.program_id ?? undefined,
          bot_name: form.bot_name || undefined,
          color_palette: form.color_palette ?? undefined,
          avatar_url: form.avatar_url || undefined,
          max_auth_attempts: form.max_auth_attempts ?? undefined,
          session_timeout_seconds: form.session_timeout_seconds ?? undefined,
        },
      });
      navigate("/campaigns");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !c) {
    return <div className="py-12 text-center">Loading...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Edit Campaign: {c.name}
        </h2>
        <p className="text-sm text-gray-500">Update campaign configuration</p>
      </div>

      <form
        className="bg-white shadow sm:rounded-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Name"
              type="text"
              value={form.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              variant="rounded-md"
              required
            />
          </div>
          <div>
            <Input
              label="Client ID"
              type="number"
              value={form.client_id ?? 0}
              onChange={(e) =>
                handleChange(
                  "client_id",
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              variant="rounded-md"
              required
            />
          </div>
        </div>
        <div>
          <Textarea
            label="Description"
            value={form.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            variant="rounded-md"
            rows={3}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Program ID"
              type="number"
              value={form.program_id ?? ""}
              onChange={(e) =>
                handleChange(
                  "program_id",
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              variant="rounded-md"
            />
          </div>
          <div>
            <Input
              label="Bot Name"
              type="text"
              value={form.bot_name || ""}
              onChange={(e) => handleChange("bot_name", e.target.value)}
              variant="rounded-md"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Avatar URL"
              type="text"
              value={form.avatar_url || ""}
              onChange={(e) => handleChange("avatar_url", e.target.value)}
              variant="rounded-md"
            />
          </div>
          <div>
            <Input
              label="Max Auth Attempts"
              type="number"
              min={0}
              value={form.max_auth_attempts ?? 3}
              onChange={(e) =>
                handleChange(
                  "max_auth_attempts",
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              variant="rounded-md"
            />
          </div>
        </div>
        <div>
          <Input
            label="Session Timeout (seconds)"
            type="number"
            min={0}
            value={form.session_timeout_seconds ?? 300}
            onChange={(e) =>
              handleChange(
                "session_timeout_seconds",
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
            variant="rounded-md"
          />
        </div>
        <div>
          <Textarea
            label="Color Palette (JSON)"
            value={JSON.stringify(form.color_palette ?? {}, null, 2)}
            onChange={(e) => {
              try {
                const parsed = e.target.value
                  ? JSON.parse(e.target.value)
                  : undefined;
                setForm((prev) => ({ ...prev, color_palette: parsed }));
              } catch (_) {}
            }}
            variant="rounded-md"
            className="font-mono"
            rows={6}
          />
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="btn-secondary px-4 py-2 rounded-md"
            onClick={() => navigate("/campaigns")}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-4 py-2 rounded-md"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      {c?.id ? (
        <AuditInfo
          resource="campaigns"
          id={c.id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
}
